import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
} from "@chakra-ui/react";
import {
  FaEdit,
  FaCheck,
  FaSearch,
  FaUsers,
  FaFileDownload,
  FaFileUpload,
} from "react-icons/fa";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { AdSetFormData } from "../../forms/adset";
import EditLocationsWidget from "../EditLocationsWidget";
import EditInterestsWidget from "../EditInterestsWidget";
import { StepProps } from "../../types";
import AudienceCard from "../AudienceCard";
import { useAudiences } from "../../hooks/useAudiences";
import { AudienceData } from "@common/interfaces/KYDMarketing";

const GENDER_OPTIONS = [
  { key: "all", value: "all" },
  { key: "male", value: "male" },
  { key: "female", value: "female" },
];

const ManualAudienceContent = ({
  search,
  loading = {},
  instagramAccounts,
  pages,
}: {
  search?: StepProps["search"];
  loading?: Record<string, boolean>;
  instagramAccounts?: Array<{
    id: string;
    username: string;
  }>;
  pages?: Array<{
    id: string;
    name: string;
  }>;
}) => {
  const { setValue, watch } = useFormContext<AdSetFormData>();
  const [isEditingLocations, setIsEditingLocations] = useState(false);
  const [isEditingInterests, setIsEditingInterests] = useState(false);
  const targetingOptions = [
    ...(watch("geo_markets") || []).map((g) => ({
      type: "geo_markets",
      value: g.key,
      name: g.value,
    })),
    ...(watch("countries") || []).map((g) => ({
      type: "countries",
      value: g.key,
      name: g.value,
    })),
  ];
  const interestsOptions = (watch("interests") || []).map((i) => ({
    type: "interests",
    value: i.key,
    name: i.value,
  }));

  const { state: audiencesState, actions: audiencesActions } = useAudiences();

  const ad_account_id = watch("account_id");
  const isEdit = !!watch("id");

  useEffect(() => {
    if (ad_account_id) {
      audiencesActions.list({
        ad_account_id: ad_account_id,
        next: false,
      });
    }
  }, [audiencesActions.list, ad_account_id]);

  return (
    <VStack>
      <AudienceCard
        fieldName="custom_audiences"
        loading={{
          campaigns: loading.campaigns,
          audiences: audiencesState.loading.audiences,
        }}
        isEdit={isEdit}
        audiences={watch("custom_audiences") as AudienceData[]}
        ad_account_id={ad_account_id}
        instagramAccounts={instagramAccounts}
        pages={pages}
      />

      {/* Form Sections */}
      <VStack w="full" spacing={4} align="stretch">
        {/* Locations Section */}
        <Box
          p={4}
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
        >
          <Stack
            w="full"
            justifyContent="space-between"
            alignItems="center"
            direction={[isEditingLocations ? "column" : "row"]}
          >
            {isEditingLocations ? (
              <EditLocationsWidget isLoading={loading.search} search={search} />
            ) : (
              <>
                <VStack align="flex-start" spacing={0}>
                  <Text fontWeight="bold" fontSize="md" color="gray.800">
                    Locations
                  </Text>
                  {targetingOptions.length > 0 && (
                    <Text fontSize="sm" color="gray.600">
                      Inclusion:{" "}
                      {targetingOptions
                        .map((t) => t.name || t.value)
                        .join(", ")}
                    </Text>
                  )}
                </VStack>
              </>
            )}
            <Button
              size="sm"
              variant="ghost"
              leftIcon={
                isEditingLocations ? (
                  <FaCheck size={12} />
                ) : (
                  <FaEdit size={12} />
                )
              }
              colorScheme="gray"
              onClick={() => setIsEditingLocations(!isEditingLocations)}
            >
              {isEditingLocations ? "Save" : "Edit"}
            </Button>
          </Stack>
        </Box>

        <HStack w="full" spacing={4}>
          {/* Age Section */}
          <Box
            p={4}
            rounded="xl"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            flexGrow={1}
          >
            <Text fontWeight="bold" fontSize="md" color="gray.800" mb={2}>
              Age
            </Text>
            <HStack w="full" spacing={2}>
              <Select
                value={watch("age_min")}
                onChange={(e) => setValue("age_min", Number(e.target.value))}
                size="md"
                bg="white"
                borderRadius="md"
              >
                {Array.from({ length: 83 }, (_, i) => i + 13).map((age) => (
                  <option key={age} value={age.toString()}>
                    {age}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.500">
                -
              </Text>
              <Select
                value={watch("age_max")}
                onChange={(e) => setValue("age_max", Number(e.target.value))}
                size="md"
                bg="white"
                borderRadius="md"
              >
                {Array.from({ length: 83 }, (_, i) => i + 13).map((age) => (
                  <option key={age} value={age.toString()}>
                    {age}
                  </option>
                ))}
              </Select>
            </HStack>
          </Box>

          {/* Gender Section */}
          <Box
            p={4}
            rounded="xl"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            flexGrow={1}
          >
            <Text fontWeight="bold" fontSize="md" color="gray.800" mb={2}>
              Gender
            </Text>
            <Select
              value={watch("genders") || "all"}
              onChange={(e) => setValue("genders", e.target.value)}
              size="md"
              bg="white"
              borderRadius="md"
            >
              {GENDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value}
                </option>
              ))}
            </Select>
          </Box>
        </HStack>

        {/* Interests Section */}
        <Box
          p={4}
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
        >
          <Stack
            w="full"
            justifyContent="space-between"
            alignItems="center"
            direction={[isEditingInterests ? "column" : "row"]}
          >
            {isEditingInterests ? (
              <EditInterestsWidget isLoading={loading.search} search={search} />
            ) : (
              <>
                <VStack align="flex-start" spacing={0}>
                  <Text fontWeight="bold" fontSize="md" color="gray.800">
                    Interests
                  </Text>
                  {interestsOptions.length > 0 && (
                    <Text fontSize="sm" color="gray.600">
                      {interestsOptions.length} interest
                      {interestsOptions.length !== 1 ? "s" : ""} selected
                    </Text>
                  )}
                </VStack>
              </>
            )}
            <Button
              size="sm"
              variant="ghost"
              leftIcon={
                isEditingInterests ? (
                  <FaCheck size={12} />
                ) : (
                  <FaEdit size={12} />
                )
              }
              colorScheme="gray"
              onClick={() => setIsEditingInterests(!isEditingInterests)}
            >
              {isEditingInterests ? "Save" : "Edit"}
            </Button>
          </Stack>
        </Box>
      </VStack>
    </VStack>
  );
};

export default ManualAudienceContent;
